import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartColumn, CircleCheck, CircleDot, Timer } from 'lucide-react';

import { queryKeys } from '../../api/queryKeys';
import { getBurndown, getSummary } from '../../api/reports';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Page, PageHeader } from '../../components/Page';
import { Select, type SelectOption } from '../../components/Select';
import { Skeleton } from '../../components/Skeleton';
import { useProject } from '../../hooks/useProject';
import { humanizeError } from '../../lib/errors';
import { formatPoints } from '../../lib/format';
import { useSprints } from '../board/useSprints';
import styles from './ReportsPage.module.css';

const RANGES = [7, 30, 90] as const;
type Range = (typeof RANGES)[number];

/** `/projects/:key/reports` — сводка, burndown и распределение (docs/06 §3.5). */
export function ReportsPage() {
  const { key = '' } = useParams<{ key: string }>();
  const { project } = useProject(key);
  const [days, setDays] = useState<Range>(30);
  const sprints = useSprints(key);
  const [sprintId, setSprintId] = useState<string | null>(null);

  const summaryQuery = useQuery({
    queryKey: queryKeys.reports.summary(key, days),
    queryFn: ({ signal }) => getSummary(key, days, signal),
    enabled: key.length > 0,
  });

  const activeSprintId = useMemo(() => {
    if (sprintId) {
      return sprintId;
    }
    const active = sprints.find((sprint) => sprint.state === 'active');
    return active?.id ?? sprints.find((sprint) => sprint.state === 'closed')?.id ?? null;
  }, [sprintId, sprints]);

  const burndownQuery = useQuery({
    queryKey: queryKeys.reports.burndown(key, activeSprintId ?? ''),
    queryFn: ({ signal }) => getBurndown(key, activeSprintId ?? '', signal),
    enabled: key.length > 0 && activeSprintId !== null,
  });

  const summary = summaryQuery.data ?? null;
  const burndown = burndownQuery.data ?? null;

  const sprintOptions: readonly SelectOption<string>[] = sprints.map((sprint) => ({
    value: sprint.id,
    label: `${sprint.name}${sprint.state === 'active' ? ' (активный)' : ''}`,
  }));

  const burndownData = useMemo(() => {
    if (!burndown) {
      return [];
    }
    const actualByDate = new Map(burndown.actual.map((point) => [point.date, point]));
    return burndown.ideal.map((point) => {
      const actual = actualByDate.get(point.date);
      return {
        date: point.date.slice(5),
        ideal: point.value,
        actual: actual?.value ?? null,
        scopeChange: actual?.scopeChange ?? 0,
      };
    });
  }, [burndown]);

  const assigneeData = useMemo(
    () =>
      (summary?.byAssignee ?? []).map((entry) => ({
        name: entry.user?.displayName ?? 'Не назначен',
        count: entry.count,
        points: entry.points,
      })),
    [summary?.byAssignee],
  );

  if (project && project.boardType !== 'scrum') {
    return (
      <Page width="wide">
        <EmptyState
          icon={<ChartColumn size={20} />}
          title="Отчёты доступны только для скрам-проектов"
          description="Смените тип доски в настройках проекта."
        />
      </Page>
    );
  }

  return (
    <Page width="wide">
      <PageHeader
        title="Отчёты"
        actions={
          <div className={styles['range']} role="group" aria-label="Период отчёта">
            {RANGES.map((value) => (
              <button
                key={value}
                type="button"
                className={value === days ? styles['rangeActive'] : styles['rangeButton']}
                aria-pressed={value === days}
                onClick={() => setDays(value)}
              >
                {value} дней
              </button>
            ))}
          </div>
        }
      />

      {summaryQuery.isPending ? (
        <div className={styles['cards']}>
          <Skeleton variant="card" count={4} />
        </div>
      ) : summaryQuery.isError ? (
        <EmptyState
          icon={<ChartColumn size={20} />}
          title="Не удалось загрузить отчёт"
          description={humanizeError(summaryQuery.error).message}
          action={
            <Button variant="primary" onClick={() => void summaryQuery.refetch()}>
              Повторить
            </Button>
          }
        />
      ) : summary ? (
        <>
          <div className={styles['cards']}>
            <MetricCard icon={<CircleDot size={16} />} label="Открыто" value={String(summary.open)} />
            <MetricCard icon={<Timer size={16} />} label={`Создано за ${days} дней`} value={String(summary.createdLast30)} />
            <MetricCard icon={<CircleCheck size={16} />} label={`Выполнено за ${days} дней`} value={String(summary.doneLast30)} />
            <MetricCard
              icon={<ChartColumn size={16} />}
              label="Всего задач"
              value={String(summary.total)}
              hint={`выполнено ${summary.done}`}
            />
          </div>

          <section className={styles['panel']} aria-label="Диаграмма сгорания">
            <div className={styles['panelHeader']}>
              <h2>Диаграмма сгорания</h2>
              {sprintOptions.length > 0 ? (
                <div className={styles['sprintSelect']}>
                  <Select
                    options={sprintOptions}
                    value={activeSprintId}
                    placeholder="Спринт не выбран"
                    onChange={setSprintId}
                  />
                </div>
              ) : null}
            </div>

            {activeSprintId === null ? (
              <p className={styles['hint']}>Нет спринтов — диаграмма появится после создания спринта.</p>
            ) : burndownQuery.isPending ? (
              <Skeleton variant="card" count={1} />
            ) : !burndown ? (
              <p className={styles['hint']}>Не удалось загрузить диаграмму.</p>
            ) : (
              <>
                <div className={styles['chart']}>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={burndownData} margin={{ top: 8, right: 16, bottom: 8, left: -12 }}>
                      <CartesianGrid stroke="var(--c-border)" strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--c-text-muted)' }} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--c-text-muted)' }} />
                      <ChartTooltip
                        contentStyle={{
                          background: 'var(--c-surface)',
                          border: '1px solid var(--c-border)',
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line
                        type="linear"
                        dataKey="ideal"
                        name="Идеальная линия"
                        stroke="var(--c-text-muted)"
                        strokeDasharray="5 4"
                        dot={false}
                        isAnimationActive={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="actual"
                        name="Факт"
                        stroke="var(--c-primary)"
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        isAnimationActive={false}
                      />
                      {burndownData
                        .filter((point) => point.scopeChange !== 0)
                        .map((point) => (
                          <ReferenceDot
                            key={point.date}
                            x={point.date}
                            y={point.actual ?? 0}
                            r={4}
                            fill="var(--c-warning)"
                            stroke="var(--c-surface)"
                          />
                        ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className={styles['hint']}>
                  {burndown.unit === 'points' ? 'В story points' : 'В задачах'} · заложено {formatPoints(burndown.totals.committed)},
                  выполнено {formatPoints(burndown.totals.completed)}, добавлено {burndown.totals.added}, снято {burndown.totals.removed}.
                  Оранжевые маркеры — изменение объёма.
                </p>
              </>
            )}
          </section>

          <section className={styles['panel']} aria-label="Распределение по исполнителям">
            <h2>Распределение по исполнителям</h2>
            {assigneeData.length === 0 ? (
              <p className={styles['hint']}>Нет назначенных задач.</p>
            ) : (
              <div className={styles['chart']}>
                <ResponsiveContainer width="100%" height={Math.max(160, assigneeData.length * 34)}>
                  <BarChart data={assigneeData} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
                    <CartesianGrid stroke="var(--c-border)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--c-text-muted)' }} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={140}
                      tick={{ fontSize: 11, fill: 'var(--c-text-muted)' }}
                    />
                    <ChartTooltip
                      contentStyle={{
                        background: 'var(--c-surface)',
                        border: '1px solid var(--c-border)',
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    />
                    <Bar dataKey="count" name="Задачи" fill="var(--c-primary)" radius={[0, 4, 4, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>
        </>
      ) : null}
    </Page>
  );
}

function MetricCard({
  icon,
  label,
  value,
  hint,
}: {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
}) {
  return (
    <article className={styles['card']}>
      <span className={styles['cardIcon']} aria-hidden>
        {icon}
      </span>
      <span className={styles['cardValue']}>{value}</span>
      <span className={styles['cardLabel']}>{label}</span>
      {hint ? <span className={styles['cardHint']}>{hint}</span> : null}
    </article>
  );
}
